/**
 * MaternityLetterGenerator.jsx
 * 
 * Modal for generating maternity-related letters.
 * Integrates with the document generator system.
 * Styled with MUI components.
 */

import React, { useState } from 'react';
import api from '../axiosConfig';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    Stepper,
    Step,
    StepLabel,
    TextField
} from '@mui/material';
import {
    Close as CloseIcon,
    Description as LetterIcon,
    CheckCircle as CheckIcon,
    Send as SendIcon,
    Email as EmailIcon,
    Print as PrintIcon,
    Download as DownloadIcon
} from '@mui/icons-material';

const LETTER_TYPES = [
    {
        id: 'LETTER-MAT-001',
        name: 'Maternity Leave Acknowledgement',
        description: 'Confirms receipt of notification and expected return date. LEGAL REQUIREMENT - must send within 28 days.',
        required: true,
        color: '#ef4444'
    },
    {
        id: 'LETTER-MAT-002',
        name: 'Return Confirmation Request',
        description: 'Request employee confirms their return to work date.',
        required: false,
        color: '#3b82f6'
    },
    {
        id: 'LETTER-MAT-003',
        name: 'KIT Day Invitation',
        description: 'Invite employee to attend a keeping in touch day.',
        required: false,
        color: '#8b5cf6'
    },
    {
        id: 'LETTER-MAT-004',
        name: 'Extension Deadline Reminder',
        description: 'Remind employee of the deadline to request extended leave.',
        required: false,
        color: '#f59e0b'
    },
    {
        id: 'LETTER-MAT-005',
        name: 'Return to Work Reminder',
        description: 'Remind employee of their upcoming return date.',
        required: false,
        color: '#22c55e'
    },
    {
        id: 'LETTER-MAT-006',
        name: 'Final Reminder (Recorded)',
        description: 'Final reminder before expected return. Should be sent recorded delivery.',
        required: false,
        color: '#ef4444'
    }
];

const MaternityLetterGenerator = ({ open, onClose, maternityId, record, onSuccess }) => {
    const [step, setStep] = useState(0);
    const [selectedLetter, setSelectedLetter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [deliveryMethod, setDeliveryMethod] = useState('email');
    const [additionalFields, setAdditionalFields] = useState({});

    const steps = ['Select Letter', 'Configure', 'Generate'];

    const handleSelectLetter = (letter) => {
        setSelectedLetter(letter);
        setStep(1);
    };

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await api.post(
                `/maternity/${maternityId}/generate-letter/${selectedLetter.id}`,
                {
                    send_method: deliveryMethod,
                    ...additionalFields
                }
            );

            setResult(res.data);

            // Log the communication
            await api.post(`/maternity/${maternityId}/communication`, {
                type: 'letter',
                method: deliveryMethod,
                direction: 'outbound',
                summary: `Generated ${selectedLetter.name}`
            });

            setStep(2);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate letter');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(0);
        setSelectedLetter(null);
        setResult(null);
        setError(null);
        setAdditionalFields({});
        onClose();
    };

    const handleComplete = () => {
        handleClose();
        if (onSuccess) onSuccess();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const renderStepContent = () => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Select the letter you want to generate for {record?.employee_name}
                        </Typography>
                        <Grid container spacing={2}>
                            {LETTER_TYPES.map((letter) => (
                                <Grid item xs={12} key={letter.id}>
                                    <Card 
                                        variant="outlined"
                                        sx={{ 
                                            borderColor: selectedLetter?.id === letter.id ? letter.color : '#e5e7eb',
                                            borderWidth: selectedLetter?.id === letter.id ? 2 : 1
                                        }}
                                    >
                                        <CardActionArea onClick={() => handleSelectLetter(letter)}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                {letter.name}
                                                            </Typography>
                                                            {letter.required && (
                                                                <Chip 
                                                                    label="Required" 
                                                                    size="small" 
                                                                    color="error" 
                                                                    sx={{ height: 20, fontSize: 10 }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {letter.description}
                                                        </Typography>
                                                    </Box>
                                                    <LetterIcon sx={{ color: letter.color, ml: 2 }} />
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f5f3ff' }}>
                            <CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7c3aed', mb: 1 }}>
                                    {selectedLetter?.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedLetter?.description}
                                </Typography>
                            </CardContent>
                        </Card>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                            Delivery Method
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={4}>
                                <Card 
                                    variant="outlined"
                                    sx={{ 
                                        borderColor: deliveryMethod === 'email' ? '#8b5cf6' : '#e5e7eb',
                                        borderWidth: deliveryMethod === 'email' ? 2 : 1,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setDeliveryMethod('email')}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <EmailIcon sx={{ color: deliveryMethod === 'email' ? '#8b5cf6' : '#9ca3af', mb: 1 }} />
                                        <Typography variant="body2" sx={{ fontWeight: deliveryMethod === 'email' ? 600 : 400 }}>
                                            Email
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={4}>
                                <Card 
                                    variant="outlined"
                                    sx={{ 
                                        borderColor: deliveryMethod === 'post' ? '#8b5cf6' : '#e5e7eb',
                                        borderWidth: deliveryMethod === 'post' ? 2 : 1,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setDeliveryMethod('post')}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <SendIcon sx={{ color: deliveryMethod === 'post' ? '#8b5cf6' : '#9ca3af', mb: 1 }} />
                                        <Typography variant="body2" sx={{ fontWeight: deliveryMethod === 'post' ? 600 : 400 }}>
                                            Post
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={4}>
                                <Card 
                                    variant="outlined"
                                    sx={{ 
                                        borderColor: deliveryMethod === 'hand' ? '#8b5cf6' : '#e5e7eb',
                                        borderWidth: deliveryMethod === 'hand' ? 2 : 1,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setDeliveryMethod('hand')}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <PrintIcon sx={{ color: deliveryMethod === 'hand' ? '#8b5cf6' : '#9ca3af', mb: 1 }} />
                                        <Typography variant="body2" sx={{ fontWeight: deliveryMethod === 'hand' ? 600 : 400 }}>
                                            Hand
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                            Letter Details
                        </Typography>
                        <Card variant="outlined">
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Employee</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {record?.employee_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Due Date</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {formatDate(record?.ewc)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Leave Starts</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {formatDate(record?.intended_start_date)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {formatDate(record?.expected_return_date)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Additional fields for specific letter types */}
                        {selectedLetter?.id === 'LETTER-MAT-003' && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                    KIT Day Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            type="date"
                                            label="Proposed Date"
                                            value={additionalFields.kit_date || ''}
                                            onChange={(e) => setAdditionalFields({ ...additionalFields, kit_date: e.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Purpose"
                                            value={additionalFields.kit_purpose || ''}
                                            onChange={(e) => setAdditionalFields({ ...additionalFields, kit_purpose: e.target.value })}
                                            placeholder="e.g., Team meeting"
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                );

            case 2:
                return (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CheckIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Letter Generated Successfully
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {result?.filename || 'Document has been created and saved to the vault.'}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            {result?.download_url && (
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    href={result.download_url}
                                    target="_blank"
                                >
                                    Download
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleComplete}
                                sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}
                            >
                                Done
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LetterIcon sx={{ color: '#8b5cf6' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Generate Letter
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Stepper */}
                <Stepper activeStep={step} sx={{ mb: 4 }}>
                    {steps.map((label, index) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                )}

                {renderStepContent()}
            </DialogContent>

            {step < 2 && (
                <DialogActions sx={{ p: 2 }}>
                    {step > 0 && (
                        <Button onClick={() => setStep(step - 1)} disabled={loading}>
                            Back
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    {step === 1 && (
                        <Button
                            variant="contained"
                            onClick={handleGenerate}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LetterIcon />}
                            sx={{ 
                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                '&:hover': { opacity: 0.9 }
                            }}
                        >
                            {loading ? 'Generating...' : 'Generate Letter'}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default MaternityLetterGenerator;